import { inject, Injectable } from '@angular/core';

import { ENV_CONFIG } from '@bn/primitives';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Cap: any; // Declare the external library

/**
 * Service for loading the CAPTCHA widget script.
 * This service ensures that the CAPTCHA widget script is loaded only once
 * and provides a method to load the script dynamically.
 */
@Injectable({ providedIn: 'root' })
export class CaptchaCapWidgetService {
  #captchaLoaded = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #capInstance?: any;
  #challengeUrl = inject(ENV_CONFIG).captchaWidgetChallengeUrl;

  get hiddenCapInstance() {
    return this.#capInstance;
  }

  /**
   * Loads the CAPTCHA widget script.
   *
   * This method creates a script element, sets its source to the CAPTCHA widget URL,
   * and appends it to the document body. It returns a promise that resolves when the
   * script is successfully loaded or rejects if there is an error loading the script.
   *
   * @returns {Promise<void>} A promise that resolves when the script is loaded.
   * @throws {string} An error message if the script fails to load.
   */
  loadCaptcha(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.#captchaLoaded) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      // This is a pinned version; changing this should be done after checking if the API supports it.
      script.src = 'https://cdn.jsdelivr.net/npm/@cap.js/widget@0.1.25';
      script.async = true;

      script.onload = () => {
        this.#captchaLoaded = true;

        if (!this.#capInstance) {
          this.#capInstance = new Cap({ apiEndpoint: this.#challengeUrl });
        }

        resolve();
      };

      script.onerror = () => {
        reject('Error loading the Captcha widget!');
      };

      document.body.appendChild(script);
    });
  }
}
